import { IanaZone, IsoInstant } from '#.shared/dates/types'
import { instantToDateTime } from '#.shared/dates/utils/convert.ts'
import { DateTime, Duration } from 'luxon'
import z from 'zod'

import { Area, AreaDto, AreaID, areaFromDto } from '../../areas/models/area'
import {
  EmployeeShiftOffer,
  EmployeeShiftOfferDto,
  employeeShiftOfferFromDto,
} from '../../employee_shift/models/employeeShiftOffer'
import {
  Employee,
  EmployeeID,
  EmployeeJson,
  employeeFromDto,
} from '../../employees/models/employee'
import {
  ShiftReason,
  ShiftReasonJson,
} from '../../shift_reasons/models/shiftReason'
import { JobRole, jobRoleFromDto } from '../../job_roles/models/jobRole'
import type { JobRole as JobRoleDto } from '../../shared/api/generated/models/jobRole'
import { Skill, SkillJson, skillFromDto } from '../../skills/models/skill'
import { Tag, TagDto, tagFromDto } from '../../tags/models/tag'
import { Timesheet, TimesheetSchema } from '../../timesheets/models/timesheet'

import { ShiftDistribution } from '../types/shift.schemas'
import { ShiftStatus as NewShiftStatus, ShiftAssignment } from '../types/types'
import { RequestRound, RequestRoundInput } from './requestQueue'
import { BreakDefinition, BreakDefinitionSchema } from './shiftBreakDefinition'

export const ShiftID = z.string().brand<'ShiftID'>()
export type ShiftID = z.infer<typeof ShiftID>

export type ShiftPublishedFlag = boolean & { readonly __brand: unique symbol }
export type ShiftOpenFlag = boolean & { readonly __brand: unique symbol }
export type ShiftApprovalRequiredFlag = boolean & {
  readonly __brand: unique symbol
}

export enum ShiftStatus {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
}

export function getShiftStatus(status: number): ShiftStatus {
  switch (status) {
    case 1:
      return ShiftStatus.ACTIVE
    case 0:
      return ShiftStatus.CANCELLED
    default:
      throw new Error('Invalid shift status')
  }
}

export interface ShiftDraft {
  startDate: DateTime
  endDate: DateTime
  areaId: AreaID
}

type ShiftStatusLabel = 'active' | 'cancelled'

type ShiftAreaRelationDto = {
  data: AreaDto
} | null

type EmployeeShiftRelationDto = {
  data: EmployeeShiftOfferDto
} | null

type EmployeeShiftOffersRelationsDto = {
  data: EmployeeShiftOfferDto[]
} | null

type ShiftSkillsRelationsDto = {
  data: SkillJson[]
} | null

type ShiftCreatorRelationDto = {
  data: EmployeeJson
} | null

type ShiftReasonRelationDto = {
  data: ShiftReasonJson
} | null

type ShiftTagRelationDto = {
  data: TagDto[]
} | null

export interface ShiftDto {
  id: string

  time_zone_id: string

  status: string
  distribution: string
  published: boolean
  locked: boolean

  start_date: string
  end_date: string

  approval_required: boolean

  open: boolean

  queue: boolean
  queue_remaining?: number
  queue_interval_minutes?: number
  queue_next_run_at?: string

  note: string

  rate_amount?: number
  rate_currency?: string

  area_id: string
  area?: ShiftAreaRelationDto

  roster_employee_id: string | null
  roster_employee_offer?: EmployeeShiftRelationDto

  offers: EmployeeShiftOffersRelationsDto

  skills?: ShiftSkillsRelationsDto

  has_potential_offers?: boolean

  has_applied_offers?: boolean

  has_queue_offers?: boolean

  creator?: ShiftCreatorRelationDto

  created_at?: string | null
  updated_at?: string | null

  reminded_at?: string | null

  permissions: { can_manage: boolean }

  actions?: string[]

  reason?: ShiftReasonRelationDto

  job_role_id?: string | null
  job_role?: { data: JobRoleDto } | null

  total_duration_minutes?: number
  payable_duration_minutes?: number

  total_rate_amount?: number | null

  tags?: ShiftTagRelationDto

  break_definitions?: {
    data: BreakDefinitionSchema[]
  }

  request_rounds?: {
    data: RequestRoundInput[]
  }

  timesheet?: {
    data: TimesheetSchema | null
  }
}

export interface Shift {
  id: ShiftID

  timeZoneId: IanaZone

  status: ShiftStatusLabel
  distribution: ShiftDistribution
  published: ShiftPublishedFlag
  locked: boolean

  startDate: DateTime
  endDate: DateTime
  duration: Duration

  approvalRequired: ShiftApprovalRequiredFlag

  open: ShiftOpenFlag

  queue: boolean
  queueRemaining?: number
  queueIntervalMinutes?: number
  queueNextRunAt?: DateTime

  note: string

  rateAmount?: number
  rateCurrency?: string

  areaId: AreaID
  area?: Area

  rosterEmployeeId: EmployeeID | null
  rosterEmployeeOffer?: EmployeeShiftOffer | null

  skills?: Skill[]

  hasPotentialOffers?: boolean

  hasAppliedOffers?: boolean

  hasQueueOffers?: boolean

  creator?: Employee | null

  createdAt?: DateTime | null
  updatedAt?: DateTime | null

  remindedAt?: DateTime | null

  actions?: string[]

  reason?: ShiftReason | null

  jobRoleId?: JobRole['id'] | null
  jobRole?: JobRole | null

  totalDurationMinutes?: Duration
  payableDurationMinutes?: Duration

  totalRateAmount?: number | null

  tags?: Tag[]

  newStatus: NewShiftStatus

  title: string

  assignment: ShiftAssignment

  breakDefinitions: BreakDefinition[]

  request_rounds: RequestRound[]

  timesheet?: Timesheet | null
}

function newShiftStatus({
  status,
  published,
  approvalRequired,
  hasApplicants,
  filled,
}: {
  status: ShiftStatusLabel
  published: boolean
  approvalRequired: boolean
  hasApplicants: boolean
  filled: boolean
}): NewShiftStatus {
  if (status === 'cancelled') {
    return 'cancelled'
  }

  if (!published) {
    return 'draft'
  }

  if (filled) {
    return 'filled'
  }

  if (approvalRequired && hasApplicants) {
    return 'pending_approval'
  }

  return 'published'
}

export const createShift = (params: {
  id: ShiftID

  timeZoneId: IanaZone

  status: ShiftStatusLabel
  distribution: ShiftDistribution
  published: boolean
  locked: boolean

  startDate: DateTime
  endDate: DateTime

  approvalRequired: boolean

  open: boolean

  queue: boolean
  queueRemaining?: number
  queueIntervalMinutes?: number
  queueNextRunAt?: DateTime

  note: string

  rateAmount?: number
  rateCurrency?: string

  areaId: AreaID
  area?: Area

  rosterEmployeeId: EmployeeID | null
  rosterEmployeeOffer: EmployeeShiftOffer | null

  skills?: Skill[]

  hasPotentialOffers?: boolean

  hasAppliedOffers?: boolean

  hasQueueOffers?: boolean

  creator?: Employee | null

  createdAt?: DateTime | null
  updatedAt?: DateTime | null

  remindedAt?: DateTime | null

  actions?: string[]

  reason?: ShiftReason | null

  jobRoleId?: JobRole['id'] | null
  jobRole?: JobRole | null

  totalDurationMinutes?: Duration
  payableDurationMinutes?: Duration

  totalRateAmount?: number | null

  tags?: Tag[]

  breakDefinitions?: BreakDefinition[]

  request_rounds?: RequestRound[]

  timesheet?: Timesheet | null
}): Shift => ({
  id: params.id,

  timeZoneId: params.timeZoneId,

  status: params.status,
  distribution: params.distribution,
  published: params.published as ShiftPublishedFlag,
  locked: params.locked,

  startDate: params.startDate,
  endDate: params.endDate,
  duration: params.endDate.diff(params.startDate, 'hours'),

  approvalRequired: params.approvalRequired as ShiftApprovalRequiredFlag,

  open: params.open as ShiftOpenFlag,

  queue: params.queue,
  queueRemaining: params.queueRemaining,
  queueIntervalMinutes: params.queueIntervalMinutes,
  queueNextRunAt: params.queueNextRunAt,

  note: params.note,

  rateAmount: params.rateAmount,
  rateCurrency: params.rateCurrency,

  areaId: params.areaId,
  area: params.area,

  rosterEmployeeId: params.rosterEmployeeId,
  rosterEmployeeOffer: params.rosterEmployeeOffer,

  skills: params.skills,

  hasPotentialOffers: params.hasPotentialOffers,

  hasAppliedOffers: params.hasAppliedOffers,

  hasQueueOffers: params.hasQueueOffers,

  creator: params.creator,

  createdAt: params.createdAt,
  updatedAt: params.updatedAt,

  remindedAt: params.remindedAt,

  actions: params.actions,

  reason: params.reason,

  jobRoleId: params.jobRoleId,
  jobRole: params.jobRole,

  totalDurationMinutes: params.totalDurationMinutes,
  payableDurationMinutes: params.payableDurationMinutes,

  totalRateAmount: params.totalRateAmount,

  tags: params.tags,

  newStatus: newShiftStatus({
    status: params.status,
    published: params.published,
    approvalRequired: params.approvalRequired,
    hasApplicants: !!params.hasAppliedOffers,
    filled: !!params.rosterEmployeeId,
  }),

  title: `Week of ${params.startDate.toLocaleString({ ...DateTime.DATE_MED, year: undefined })}`,

  assignment: {
    mode:
      params.distribution === 'none'
        ? 'direct'
        : params.distribution === 'open'
          ? 'open'
          : 'targeted',
    requiresApproval: params.approvalRequired,
  },

  breakDefinitions: params.breakDefinitions ?? [],

  request_rounds: params.request_rounds ?? [],

  timesheet: params.timesheet,
})

export const shiftFromDto = (dto: ShiftDto): Shift => {
  const ianaZone = IanaZone.parse(dto.time_zone_id)
  return createShift({
    id: dto.id as ShiftID,

    timeZoneId: ianaZone,

    status: dto.status as ShiftStatusLabel,
    distribution: dto.distribution as ShiftDistribution,
    published: dto.published,
    locked: dto.locked,

    startDate: instantToDateTime(IsoInstant.parse(dto.start_date), ianaZone),
    endDate: instantToDateTime(IsoInstant.parse(dto.end_date), ianaZone),

    approvalRequired: dto.approval_required,

    open: dto.open,

    queue: dto.queue,
    queueRemaining: dto.queue_remaining,
    queueIntervalMinutes: dto.queue_interval_minutes,
    queueNextRunAt: dto.queue_next_run_at
      ? DateTime.fromISO(dto.queue_next_run_at)
      : undefined,

    note: dto.note,

    rateAmount: dto.rate_amount,
    rateCurrency: dto.rate_currency,

    areaId: dto.area_id as AreaID,
    area: dto.area?.data ? areaFromDto(dto.area.data) : undefined,

    rosterEmployeeId: dto.roster_employee_id as EmployeeID | null,
    rosterEmployeeOffer: dto.roster_employee_offer?.data
      ? employeeShiftOfferFromDto(dto.roster_employee_offer.data)
      : null,

    skills: dto.skills?.data ? dto.skills.data.map(skillFromDto) : undefined,

    hasPotentialOffers: dto.has_potential_offers,

    hasAppliedOffers: dto.has_applied_offers,

    hasQueueOffers: dto.has_queue_offers,

    creator: dto.creator?.data ? employeeFromDto(dto.creator.data) : null,

    createdAt: dto.created_at ? DateTime.fromISO(dto.created_at) : null,
    updatedAt: dto.updated_at ? DateTime.fromISO(dto.updated_at) : null,

    remindedAt: dto.reminded_at ? DateTime.fromISO(dto.reminded_at) : null,

    actions: dto.actions,

    reason: dto.reason?.data ? ShiftReason.fromJson(dto.reason.data) : null,

    jobRoleId: (dto.job_role_id ?? null) as JobRole['id'] | null,
    jobRole: dto.job_role?.data ? jobRoleFromDto(dto.job_role.data) : null,

    totalDurationMinutes: dto.total_duration_minutes
      ? Duration.fromObject({ minutes: dto.total_duration_minutes })
      : undefined,
    payableDurationMinutes: dto.payable_duration_minutes
      ? Duration.fromObject({ minutes: dto.payable_duration_minutes })
      : undefined,

    totalRateAmount: dto.total_rate_amount,

    tags: dto.tags?.data ? dto.tags.data.map(tagFromDto) : undefined,

    breakDefinitions: dto.break_definitions?.data.map((b) =>
      BreakDefinition.parse(b),
    ),

    request_rounds: (dto.request_rounds?.data ?? []).map((round) =>
      RequestRound.parse(round),
    ),

    timesheet: dto.timesheet?.data ? Timesheet.parse(dto.timesheet.data) : null,
  })
}

// import { Shift as ShiftZod } from '#.shared/api/generated/endpoints/cliniShiftManager.schemas.zod.ts'

// export const Shift = camelize(
//   override(ShiftDTO, {
//     id: ShiftID,
//     time_zone_id: IanaZone,
//     start_date: IsoInstant,
//     end_date: IsoInstant,
//   }),
// ).transform((dto) => ({
//   ...dto,
//   startDate: DateTime.fromISO(dto.startDate, { zone: dto.timeZoneId }),
//   endDate: DateTime.fromISO(dto.endDate, { zone: dto.timeZoneId }),
// }))
